import type {
  BankReconciliation,
  CashCount,
  CreateBankReconciliationInput,
  CashTransfer,
  CashVoucher,
  CreateCashVoucherInput,
  CreateExpenseInput,
  Expense,
  RecurringExpense,
  SaveRecurringExpenseInput,
} from './cash';
import type { CashVoucherKind } from '../generated/enums';
import type { CreateCashTransferInput, SendCashTransferInput } from '../cash/transfer';
import { payInParts, type SplitPart } from '../cash/split';
import { apiFetch } from './client';

/**
 * The cash writes both clients send — the website's cash book and the phone's Cash and bank —
 * built once, on `bookingCommands.ts`'s terms.
 */

/**
 * Records a day's count, and settles the difference.
 *
 * <p>
 * There is deliberately no update call beside this, on the expense voucher's terms and one of its
 * own: the count has already posted an adjustment, so editing it would leave the ledger reconciled
 * to a figure the record no longer shows.
 * </p>
 */
export function recordCashCount(
  id: number,
  input: { counted: number; countedOn: string; notes?: string | null },
) {
  return apiFetch<CashCount>(`/api/cash/accounts/${id}/counts`, { method: 'POST', body: input });
}

/**
 * Records a bank statement as matched. Refused unless it balances exactly — see `cash/reconcile.ts`.
 * Posts nothing; there is no edit, only undoing the latest.
 */
export function reconcileBank(accountId: number, input: CreateBankReconciliationInput) {
  return apiFetch<BankReconciliation>(`/api/cash/accounts/${accountId}/reconciliations`, {
    method: 'POST',
    body: input,
  });
}

/** Takes back an account's latest reconciliation, with a reason; its rows go back to uncleared. */
export function undoBankReconciliation(id: number, reason: string) {
  return apiFetch<BankReconciliation>(`/api/cash/reconciliations/${id}/undo`, {
    method: 'POST',
    body: { reason },
  });
}

/** A recurring expense, created or rewritten wholesale (step 8). See `cash/recurring.ts`. */
export function saveRecurringExpense(id: number | null, input: SaveRecurringExpenseInput) {
  return id === null
    ? apiFetch<RecurringExpense>('/api/expense/recurring', { method: 'POST', body: input })
    : apiFetch<RecurringExpense>(`/api/expense/recurring/${id}`, { method: 'PUT', body: input });
}

/** Marks a template's next occurrence as paid some other way — only the next one, as the server insists. */
export function skipRecurringExpense(id: number, dueOn: string) {
  return apiFetch<RecurringExpense>(`/api/expense/recurring/${id}/skip`, {
    method: 'POST',
    body: { dueOn },
  });
}

/** Takes a skip back — that occurrence is due again. */
export function undoRecurringSkip(id: number, skipId: number) {
  return apiFetch<RecurringExpense>(`/api/expense/recurring/${id}/skips/${skipId}`, {
    method: 'DELETE',
  });
}

/** Records money going out. `Adjustment` is refused by the server; see `cash/expense.ts`. */
export function createExpense(input: CreateExpenseInput) {
  return apiFetch<Expense>('/api/expense', { method: 'POST', body: input });
}

/**
 * An expense paid from several accounts — **one voucher per account** (owner, 2026-11-01), the
 * requests built by `cash/expense.ts` `expenseRequests`, sent through `payInParts`: nothing saved
 * and refused throws; a refusal part-way answers what was saved.
 */
export function createExpensesInParts(
  requests: readonly CreateExpenseInput[],
  parts: readonly SplitPart[],
) {
  return payInParts(parts, (part) => {
    const request = requests.find((each) => each.cashAccountId === part.accountId);

    return request ? createExpense(request) : Promise.reject(new Error('No voucher for that box.'));
  });
}

/**
 * Cash vouchers — owner's money, drawings, other income — in several accounts: one voucher per
 * account, otherwise as `createCashVoucher`. `base` is everything but the account, amount and how.
 */
export function createCashVouchersInParts(
  base: Omit<CreateCashVoucherInput, 'amount' | 'method' | 'cashAccountId' | 'reference'> & {
    reference: string | null;
  },
  parts: readonly SplitPart[],
) {
  return payInParts(parts, (part, amount) =>
    createCashVoucher({
      ...base,
      amount,
      method: part.method,
      reference:
        [base.reference?.trim(), part.reference.trim()].filter((text) => !!text).join(' · ') ||
        null,
      cashAccountId: part.accountId,
    }),
  );
}

/** Moves money between two of the branch's accounts — the contra voucher. See `cash/transfer.ts`. */
export function createCashTransfer(input: CreateCashTransferInput) {
  return apiFetch<CashTransfer>('/api/cash/transfers', { method: 'POST', body: input });
}

/** Calls a move off with a reason; both of its rows are reversed, dated today. */
export function voidCashTransfer(id: number, reason: string) {
  return apiFetch<CashTransfer>(`/api/cash/transfers/${id}/void`, {
    method: 'POST',
    body: { reason },
  });
}

/**
 * The door a voucher goes through: owner's money is behind `OwnersMoney` (the company admin's),
 * other income behind Cash & bank — the server refuses a kind through the other door.
 */
function voucherDoor(kind: CashVoucherKind): string {
  return kind === 'OtherIncome' ? '/api/cash/vouchers/other-income' : '/api/cash/vouchers/owner';
}

/** Owner's money in, drawings out or other income. See `cash/voucher.ts`. */
export function createCashVoucher(input: CreateCashVoucherInput) {
  return apiFetch<CashVoucher>(voucherDoor(input.kind), { method: 'POST', body: input });
}

/** Calls a voucher off with a reason; its row is reversed, dated today. */
export function voidCashVoucher(id: number, kind: CashVoucherKind, reason: string) {
  return apiFetch<CashVoucher>(`${voucherDoor(kind)}/${id}/void`, {
    method: 'POST',
    body: { reason },
  });
}

/**
 * Sends money to another branch — the company administrator's (`BranchCashTransfer`). Out of this
 * branch now; in transit until the other presses Received.
 */
export function sendCashToBranch(input: SendCashTransferInput) {
  return apiFetch<CashTransfer>('/api/cash/transfers/branch', { method: 'POST', body: input });
}

/** Calls off money sent to another branch, before it is received. */
export function voidSentCashTransfer(id: number, reason: string) {
  return apiFetch<CashTransfer>(`/api/cash/transfers/branch/${id}/void`, {
    method: 'POST',
    body: { reason },
  });
}

/** Money sent here has arrived; null account is the branch's default for the way it travelled. */
export function receiveCashTransfer(
  id: number,
  input: { receivedOn: string; toAccountId: number | null },
) {
  return apiFetch<CashTransfer>(`/api/cash/transfers/${id}/receive`, {
    method: 'POST',
    body: input,
  });
}
